import { Box, Button, Card, CardContent, CardHeader, Grid, MenuItem, TextField, Typography } from '@mui/material';
import api from 'api';
import BodyTemplate from 'components/_layout/navigation/BodyTemplate';
import { GenericPaginator } from 'components/generic-table';
import ContextLink from 'components/_base/ContextLink';
import FindPatientsForm from 'components/find-patients-form';
import PatientList from 'components/patient-list';
import { useNotification } from 'hooks';
import { Patient, PatientsListRequest, PatientsListResponse } from 'models/patients.model';
import { PrescriptionProfileListResponse } from 'models/prescription.model';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GlobalState } from 'store/reducers';
import { DEFAULT_PAGINATION_SIZE } from 'utils/Constants';

const Prescriptions: FC = () => {
  const { t } = useTranslation();
  const { notification } = useNotification();

  const currentUser = useSelector((state: GlobalState) => state.session.authenticateUser);

  const formData: PatientsListRequest = {
    first_name: undefined,
    last_name: undefined,
    cns: undefined,
    gender: undefined,
    birth_name: undefined,
    birth_date: undefined,
  };

  const [form, setForm] = useState(formData);
  const [pdfData, setPdfData] = useState<Blob>();

  const handleFormData = (data: PatientsListRequest) => {
    setForm(data);
    const resetedPaginator = { ...paginator, page: 1 };
    setPaginator(resetedPaginator);
    requestPatientList(resetedPaginator, data);
  };

  const paginatorData: GenericPaginator = {
    total: 0,
    size: DEFAULT_PAGINATION_SIZE,
    page: 1,
  };

  const [paginator, setPaginator] = useState(paginatorData);
  const patientListData: Patient[] | undefined = undefined;
  const [patientList, setPatientList] = useState<Patient[] | undefined>(patientListData);
  const [selectedProfile, setSelectedProfile] = useState<string>('');

  const handlePaginatorData = (data: GenericPaginator) => {
    setPaginator(data);
    requestPatientList(data, form);
  };

  const requestPatientList = useCallback(
    (paginator: GenericPaginator, form: PatientsListRequest) => {
      api.patients
        .getPatients(form, paginator.size, paginator.page)
        .then((response: PatientsListResponse) => {
          setPatientList(response.results);
          setPaginator({
            total: response.total,
            size: response.size,
            page: response.page,
          });
        })
        .catch(() => {
          notification(t('prescriptions.notification.error'), 'error');
        });
    },
    [notification, t],
  );

  const [prescriptionProfiles, setPrescriptionProfiles] = useState<PrescriptionProfileListResponse>([]);

  useEffect(() => {
    api.prescriptions
      .getEditablePrescriptionsProfilesByDoctorId({
        ms_id: currentUser!.id,
      })
      .then((response: PrescriptionProfileListResponse) => {
        setPrescriptionProfiles(response);
      })
      .catch(() => notification(t('prescriptions.profiles.notification.error'), 'error'));
  }, [currentUser, notification, t]);

  useEffect(() => {
    api.prescriptions
      .getPrescriptionFileForDoctor()
      .then((data) => {
        setPdfData(data);
      })
      .catch(() => notification(t('prescriptions.types.error'), 'error'));
  }, [notification, t]);

  const files = pdfData && new Blob([pdfData], { type: 'application/pdf' });
  const fileURL = files && URL.createObjectURL(files);

  return (
    <BodyTemplate title={t('bodyTemplate.doctor.prescriptions')}>
      <Grid container columnSpacing={2} rowSpacing={3}>
        <Grid item xs={12} md={6}>
          <FindPatientsForm halfWidth handleFormAction={handleFormData} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ marginBottom: (theme) => theme.spacing(4) }}>
            <CardHeader
              title={
                <Typography variant='h2' mb={2}>
                  {t('prescriptions.profiles.title')}
                </Typography>
              }
            />
            <CardContent>
              <Typography variant='body1' mb={4}>
                {t('prescriptions.profiles.subtitle')}
              </Typography>
              <Grid container item xs={12}>
                <Grid container item spacing={{ xs: 2, md: 4 }}>
                  <Grid item xs={12}>
                    <TextField select variant='filled' label={`${t('prescriptions.profiles.field.profile_name')}`} defaultValue={''} onChange={(e) => setSelectedProfile(e.target.value)} fullWidth>
                      {prescriptionProfiles.map(({ id, name }, i) => {
                        return (
                          <MenuItem value={id} key={`profile-${i}`}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '20px',
                  mt: 6,
                }}
              >
                <ContextLink to='DOCTOR_PRESCRIPTION_PROFILES_NEW'>
                  <Button type='button' variant='contained'>
                    {t('prescriptions.profiles.new_profile')}
                  </Button>
                </ContextLink>
                <ContextLink to='DOCTOR_PRESCRIPTION_PROFILES_UPDATE' params={selectedProfile !== '' ? parseInt(selectedProfile) : undefined} disabled={selectedProfile === ''}>
                  <Button type='submit' variant='contained' disabled={selectedProfile === ''}>
                    {t('common.action.modify')}
                  </Button>
                </ContextLink>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <Grid container>
              <Grid item xs={6}>
                <CardHeader
                  title={
                    <Typography variant='h2' mb={2}>
                      {t('prescriptions.types.title')}
                    </Typography>
                  }
                />
                <CardContent>
                  <Typography variant='body1'>{t('prescriptions.types.subtitle')}</Typography>
                </CardContent>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <a href={fileURL} download style={{ textDecoration: 'none' }}>
                    <Button type='submit' variant='contained'>
                      {t('common.action.pdfDownload')}
                    </Button>
                  </a>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <PatientList
            patientDetailAction={{
              to: 'DOCTOR_PATIENTS_DETAILS',
            }}
            showPrescriptionAction
            patientList={patientList}
            paginatorData={paginator}
            handlePaginatorAction={handlePaginatorData}
          />
        </Grid>
      </Grid>
    </BodyTemplate>
  );
};

export default Prescriptions;
